import type { Enrollment, MembershipItem } from '@spike/membership-model';
import {
    computeMembershipDisplayPrice,
    computeMembershipPreviewDiscount,
    getMembershipRemainingUses,
    isEnrollmentAvailableOnDate,
    resolveMembershipBasePrice,
    shouldApplyMembershipToAllAppointments
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Walking, DataVaraible, PetServiceWrapper } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { getWalkingDateSlotsForSave } from 'utils/walkingDateSlots';

const enrollmentFromWalking = (walking: Walking): Enrollment | undefined => walking.enrollment;

const membershipItemFromWalking = (walking: Walking): MembershipItem | undefined => walking.membershipItem;

export const buildWalkingVariableDataPerSlot = (
    walking: Walking,
    fallbackDate: Moment,
    timezone: string,
    isEditMode = false,
    membershipUsesAlreadyAllocated = 0,
    catalogPriceOverride?: number
): Array<DataVaraible> => {
    const durationInMinutes =
        walking.service?.duration || (walking.from && walking.to ? walking.to.diff(walking.from, 'minutes') : 0) || 0;
    const walkingEnrollment = enrollmentFromWalking(walking);
    const membershipItem = membershipItemFromWalking(walking);
    const catalogPrice = catalogPriceOverride ?? walking.service?.price ?? walking.price ?? 0;
    const slotLinePrice = walking.price || walking.service?.price || catalogPrice || 0;

    const baseWalkingService: PetServiceWrapper = {
        service: {
            id: walking.service?.serviceId || 0,
            name: walking.service?.serviceName || 'Walking Service'
        },
        price: slotLinePrice,
        beforePrice: catalogPrice > 0 ? catalogPrice : walking.service?.price ?? walking.price,
        durationInMinutes: durationInMinutes || 0,
        duration: {
            hours: durationInMinutes ? Math.trunc(durationInMinutes / 60) : 0,
            minutes: durationInMinutes ? durationInMinutes % 60 : 0
        },
        discount: {
            type: walking.discount?.type || 'amount',
            value: walking.discount?.value ?? 0
        },
        businessArea: 'walking',
        color: walking.service?.color ?? '',
        address: walking.service?.inHomeService ? walking.address : '',
        instructions: walking.service?.inHomeService ? walking.instructions : undefined,
        inHomeService: walking.service?.inHomeService ?? false,
        saveCustomPrice: walking.saveCustomPrice,
        customPriceId: walking.customPriceId,
        customPriceAmount: slotLinePrice > 0 ? slotLinePrice : undefined
    };

    const walkingSlots = getWalkingDateSlotsForSave(walking, fallbackDate, timezone, isEditMode);
    const applyMembershipToAll =
        walkingEnrollment != null &&
        membershipItem != null &&
        shouldApplyMembershipToAllAppointments(membershipItem, walkingEnrollment);
    let limitedMembershipUsesRemaining =
        walkingEnrollment != null && membershipItem != null && !applyMembershipToAll
            ? Math.max(
                  0,
                  getMembershipRemainingUses(membershipItem, walkingEnrollment) - membershipUsesAlreadyAllocated
              )
            : Number.POSITIVE_INFINITY;

    return walkingSlots.map(slot => {
        const membershipAvailableOnDate =
            walkingEnrollment != null &&
            membershipItem != null &&
            isEnrollmentAvailableOnDate(walkingEnrollment, slot.date, timezone);
        let applyMembership = false;

        if (membershipAvailableOnDate) {
            if (applyMembershipToAll) {
                applyMembership = true;
            } else if (limitedMembershipUsesRemaining > 0) {
                applyMembership = true;
                limitedMembershipUsesRemaining -= 1;
            }
        }

        const serviceForSlot: PetServiceWrapper = applyMembership
            ? (() => {
                  const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                  const membershipPricing = computeMembershipDisplayPrice(membershipBase, membershipItem);
                  const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                  return {
                      ...baseWalkingService,
                      price: membershipPricing.displayPrice,
                      beforePrice: membershipBase,
                      customPriceAmount: membershipBase > 0 ? membershipBase : undefined,
                      discount: previewDiscount
                          ? { type: previewDiscount.type, value: previewDiscount.value }
                          : { type: 'amount' as const, value: 0 },
                      enrollment: walkingEnrollment,
                      membershipItem
                  };
              })()
            : {
                  ...baseWalkingService,
                  price: slotLinePrice,
                  beforePrice: catalogPrice > 0 ? catalogPrice : baseWalkingService.beforePrice,
                  discount: { type: 'amount', value: 0 },
                  enrollment: undefined,
                  membershipItem: undefined
              };

        const discountForSlot =
            walkingEnrollment != null && membershipItem != null && !applyMembership
                ? { type: 'amount' as const, value: 0 }
                : applyMembership
                ? (() => {
                      const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                      const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                      return previewDiscount
                          ? {
                                type: previewDiscount.type as 'amount' | 'percentage',
                                value: previewDiscount.value
                            }
                          : { type: 'percentage' as const, value: 100 };
                  })()
                : {
                      type: (walking.discount?.type || 'amount') as 'amount' | 'percentage',
                      value: walking.discount?.value ?? 0
                  };

        const sessionIdForSlot =
            slot.serviceSessionId !== undefined
                ? slot.serviceSessionId
                : walking.serviceSessionId != null &&
                  walking.from?.isValid() &&
                  slot.date.clone().tz(timezone).format('YYYY-MM-DD') ===
                      walking.from.clone().tz(timezone).format('YYYY-MM-DD')
                ? walking.serviceSessionId
                : null;

        return {
            service: { ...serviceForSlot, serviceSessionId: sessionIdForSlot },
            staffId: slot.staffId ?? walking.staffId,
            date: slot.date,
            dateEnd: slot.dateEnd,
            itemData: {
                discount: discountForSlot,
                flow: 'walking',
                ...(catalogPrice > 0 || slotLinePrice > 0
                    ? { price: resolveMembershipBasePrice(catalogPrice, slotLinePrice) }
                    : {})
            }
        };
    });
};
